import {useState} from "react";
import axios from "axios";

export default function useSearch(initialValue, getEndpoint, defaultSortOption = "name-asc", initialFacets = [], language = "en") {
  const [searchValue, setSearchValue] = useState(initialValue);
  const [data, setData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [openFacets, setOpenFacets] = useState(false);
  const [selectedFacets, setSelectedFacets] = useState(initialFacets);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortOption, setSortOption] = useState(defaultSortOption); // sortField=createdAt, sortOrder=desc
  const [facets, setFacets] = useState({});

  const executeSearch = async (params) => {
    setIsLoading(true);
    try {
      const url = params.toString().length > 0 ? `${getEndpoint}?${params.toString()}` : getEndpoint;
      const res = await axios.get(url);
      const resultData = res.data; // Handle different response structures
      setFacets(resultData.aggregations || {});
      setData(resultData?.hits?.hits);
      setTotalItems(resultData?.hits?.total.value);
    } catch (e) {
      console.error("Error fetching data:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchWithParams = async (search, facetsInput, pageNum, size, sort) => {
    const params = new URLSearchParams();
    params.append("page", pageNum);
    params.append("pageSize", size);
    const [field, order] = sort.split("-");
    params.append("sortField", field);
    params.append("sortOrder", order);

    if (search) params.append("search", search);

    if (facetsInput?.length > 0) {
      facetsInput.forEach((facet) => {
        params.append(`terms__${facet.name}`, facet.key);
      });
    }
    if (language && language !== "en") params.append("lang", language);

    await executeSearch(params);
  };

  const fetchFullData = async (facetsInput = selectedFacets) => {
    setIsLoading(true);

    try {
      const params = new URLSearchParams();
      // Facets
      if (facetsInput && facetsInput.length > 0) {
        facetsInput.forEach((facet) => {
          params.append(`terms__${facet.name}`, facet.key);
        });
      }
      // Language
      if (language && language !== "en") {
        params.append("lang", language);
      }

      const url = params.toString().length > 0 ? `${getEndpoint}?${params.toString()}` : getEndpoint;
      const res = await axios.get(url);

      const resultData = res.data.data;
      setFacets(resultData.aggregations || {});
      setData(resultData.hits ? resultData.hits.hits : resultData);
      setTotalItems(resultData.hits ? resultData.hits.total.value : resultData.length);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const buildSearchParams = (facetsInput = selectedFacets, connectorList = []) => {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("size", pageSize);
    const [field, order] = sortOption.split("-");
    params.append("sortField", field);
    params.append("sortOrder", order);

    if (searchValue) params.append("search", searchValue);

    if (facetsInput && facetsInput.length > 0) {
      facetsInput.forEach((facet) => {
        params.append(`terms__${facet.name}`, facet.key);
      });
    }
    // merge=true only when connectors selected
    if (connectorList.length > 0) {
      params.append("merge", "true");
    }
    if (language && language !== "en") params.append("lang", language);
    return params;
  };

  const handleSearch = async (facetsInput = selectedFacets) => {
    const params = buildSearchParams(facetsInput);
    await executeSearch(params);
  };

  const handleSearchChange = (event, shouldClearAll = true) => {
    const searchTerm = event.target.value;
    setSearchValue(searchTerm);

    if (searchTerm.length === 0 && shouldClearAll) {
      clearAllSearchFilters();
    }
  };

  const clearAllSearchFilters = () => {
    // Update local state
    setSearchValue("");
    setSelectedFacets([]);
    setPage(1);
    setOpenFacets(false);
    handleSearchWithParams("", [], 1, pageSize, defaultSortOption);
  };

  const handleFacetsOpen = () => {
    setOpenFacets(true);
  };

  const handleFacetsClose = () => {
    setOpenFacets(false);
  };

  const handleFacetsSave = (facets) => {
    setSelectedFacets(facets);
    setOpenFacets(false);
  };

  const handleClearFacets = () => {
    setSelectedFacets([]);
    setOpenFacets(false);
    const params = buildSearchParams([]); // Pass empty facets
    executeSearch(params);
  };

  const removeFacet = (facetToRemove) => {
    const updatedFacets = selectedFacets.filter((f) => !(f.name === facetToRemove.name && f.key === facetToRemove.key));
    setSelectedFacets(updatedFacets);
    // Re-fetch using the updated facets but keeping current search/page
    const params = buildSearchParams(updatedFacets);
    executeSearch(params);
  };

  return {
    searchValue,
    setSearchValue,
    data,
    totalItems,
    isLoading,
    handleSearchChange,
    handleSearch,
    clearAllSearchFilters,
    fetchFullData,
    handleFacetsSave,
    handleFacetsClose,
    handleFacetsOpen,
    handleClearFacets,
    page,
    setPage,
    pageSize,
    setPageSize,
    sortOption,
    setSortOption,
    selectedFacets,
    openFacets,
    facets,
    setSelectedFacets,
    removeFacet,
    buildSearchParams,
    handleSearchWithParams,
  };
}
